import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ShoppingBasket } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StoreFinder } from "@/components/grocery/StoreFinder";
import { formatQty } from "@/lib/demo";

export function DemoGrocery({ grocery, persons = 1, onGoMenu }) {
  const { t, tv } = useLang();
  const { user } = useAuth();
  const [checked, setChecked] = useState([]);
  const unit = (u) => (u === "pc" ? t("dm_unit_pc") : u === "slice" ? t("dm_unit_slice") : u);
  const toggle = (k) => setChecked((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  const items = [...grocery.items].sort((a, b) => tv(a.name).localeCompare(tv(b.name)));
  return (
    <section className="mt-5 chenia-card p-5 sm:p-8" aria-label={t("dm_tab_grocery")} data-testid="demo-grocery">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-chenia-sage/30 text-chenia-espresso"><ShoppingBasket className="h-5 w-5" /></span>
        <div><h2 className="font-serif text-2xl font-semibold">{t("dm_tab_grocery")}</h2>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="demo-grocery-progress">{t("demo_grocery_progress", { checked: checked.length, total: items.length })}</p></div>
      </div>
      {grocery.meals === 0 ? (
        <div className="mt-5 rounded-2xl border border-dashed border-primary/40 p-5 text-sm text-muted-foreground" data-testid="demo-grocery-empty">
          <p>{t("dm_grocery_empty")}</p>
          <Button onClick={onGoMenu} className="mt-3 rounded-full">{t("dm_compose")}</Button>
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm text-muted-foreground">{t("dm_grocery_intro", { count: grocery.meals })} · {t("dm_for_persons", { count: persons })}</p>
          <ul className="mt-4 divide-y divide-border" data-testid="demo-grocery-list">
            {items.map((item) => {
              const sel = checked.includes(item.key);
              return (
                <li key={item.key}>
                  <button type="button" role="checkbox" aria-checked={sel} onClick={() => toggle(item.key)} className="flex min-h-12 w-full items-center gap-3 py-2 text-left" data-testid={`demo-grocery-${item.key}`}>
                    <span className={"grid h-5 w-5 shrink-0 place-items-center rounded-md border " + (sel ? "border-chenia-sage bg-chenia-sage text-chenia-espresso" : "border-border")}>{sel && <Check className="h-3.5 w-3.5" />}</span>
                    <span className={"flex-1 text-sm " + (sel ? "text-muted-foreground line-through" : "text-foreground")}>{tv(item.name)}</span>
                    <span className="shrink-0 text-sm font-medium text-muted-foreground">{formatQty(item.qty)} {unit(item.unit)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <div className="mt-6 border-t border-border pt-5">
        {user ? <StoreFinder /> : (
          <div className="rounded-2xl border border-chenia-sage/40 bg-chenia-sage/15 p-4">
            <h3 className="font-serif text-lg font-semibold">{t("st_title")}</h3><p className="mt-1 text-sm text-muted-foreground">{t("st_sub")}</p>
            <p className="mt-3 text-sm leading-relaxed">{t("demo_store_access")}</p>
            <div className="mt-3 flex flex-wrap gap-2"><Link to="/register"><Button className="rounded-full">{t("demo_store_cta")}</Button></Link><Link to="/login"><Button variant="outline" className="rounded-full">{t("demo_store_login")}</Button></Link></div>
          </div>
        )}
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t("demo_drive_cart_note")}</p>
      </div>
    </section>
  );
}
